import { useParams } from 'react-router';
import { DocumentTitle } from '../../shared/components/DocumentTitle';
import { CheckoutSummary, FormDataConfirm } from '../checkout/CheckoutSummary';
import React, { useCallback, useLayoutEffect } from 'react';
import { useAccounts } from '../checkout/api/accounts/useAccounts';
import styled from 'styled-components';
import { ContainerInnerWithColumns } from '../../shared/common/ContainerInner';
import { useMeta } from './api/meta/useMeta';
import { useReserveCheckout } from './api/reserve-checkout/useReserveCheckout';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import CheckoutListItem, { CheckoutListHeader } from '../checkout/CheckoutListItem';
import ProductList from '../../shared/common/ProductList';
import { useReserveCheckoutProducts } from './api/reserve-checkout/useReserveCheckoutProducts';
import { FilterBar } from '../../shared/common/FilterBar';
import { CHECKOUT_VIEW_SORT_BY } from '../../shared/common/SortByDropDown';
import { Pagination } from '../../shared/common/Pagination';
import Utils from '../../shared/utils';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { fetchReserveListCheckoutAccountSuggestion } from './api/reserve-checkout/apiFetchAccountSuggestion';
import { t } from 'i18next';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { DISPLAY_TYPE_LIST } from '../../shared/common/displayType/types';
import { useNavigate } from 'react-router-dom';

const CheckoutContainerInner = styled(ContainerInnerWithColumns)`
    margin-left: auto;
    margin-right: auto;
`;

export const ReserveCheckout = () => {
    const navigate = useNavigate();
    const authentication = useAuthentication();

    const { reserveListId = '' } = useParams<'reserveListId'>();

    const fetchAccountSuggestionFunction = useCallback(
        () => fetchReserveListCheckoutAccountSuggestion(reserveListId),
        [reserveListId]
    );
    const {
        data: accountList,
        selectedAccount,
        onAccountChange,
        isLoading: isLoadingAccounts,
        isError: isErrorAccounts
    } = useAccounts(fetchAccountSuggestionFunction);
    const { data: meta, isLoading: isMetaLoading, isError: isMetaError } = useMeta(reserveListId);
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultPageSize: 55,
        defaultSortBy: CHECKOUT_VIEW_SORT_BY[0].key
    });

    const { mutate: confirmCheckout, isLoading: isLoadingConfirmCheckout } = useReserveCheckout(reserveListId);
    const { data: reserveCheckoutProducts, isLoading: isProductsLoading } = useReserveCheckoutProducts(reserveListId, {
        page,
        pageSize,
        sortBy
    });

    const onConfirmation = (formData: FormDataConfirm) => confirmCheckout({ meta, formData });

    useLayoutEffect(() => {
        if (!Utils.isCustomerAllowedToOrder(authentication)) {
            navigate('/');
        }
    }, [authentication, navigate]);

    if (isLoadingAccounts || isMetaLoading || isProductsLoading) {
        return (
            <LoadingWrapper>
                <LoadingOverlay loading={true} />
            </LoadingWrapper>
        );
    }

    const spoc = authentication.spoc;
    const products = reserveCheckoutProducts?.hits ?? [];
    const totalProducts = reserveCheckoutProducts?.totalHits ?? 0;
    const accounts = {
        list: accountList,
        selected: selectedAccount,
        loading: isLoadingAccounts
    };

    const standingOrders = selectedAccount?.standingOrders
        ? {
              list: selectedAccount.standingOrders,
              selected: selectedAccount.standingOrders[0],
              show: true
          }
        : undefined;

    return (
        <DocumentTitle title={t('components.documentTitle.checkout')}>
            <CheckoutContainerInner className="bw-wrap medium">
                <div className="txt-upper">{t('reserveReports.reserveReportCheckout.reserveReportOrderSummary')}</div>
                <div className="content-area-inner">
                    <CheckoutSummary
                        {...meta}
                        accounts={accounts}
                        authentication={authentication}
                        onAccountChange={onAccountChange}
                        onConfirmation={onConfirmation}
                        isPlaceOrderLoading={isLoadingConfirmCheckout}
                        isPlaceOrderDisabled={isMetaError || isErrorAccounts}
                        standingOrders={standingOrders}
                    />
                    <div className="checkout-pl">
                        <FilterBar
                            sortBy={sortBy}
                            pageSize={pageSize}
                            totalProducts={totalProducts}
                            isFilterToggleReplaced={true}
                            leftColumnText={t('shop.orders.items')}
                            hideGroupBy={true}
                            hideSelectButtons={true}
                            hideSelectAll={true}
                            hideDisplayType={true}
                            sorting={CHECKOUT_VIEW_SORT_BY}
                        />
                        <CheckoutListHeader spoc={spoc} />
                        <ProductList products={products} displayType={DISPLAY_TYPE_LIST} onAddPrice={async () => {}}>
                            {({ key, isListView, ...value }) => (
                                <CheckoutListItem key={key} product={value.product} page="reserveList" />
                            )}
                        </ProductList>
                        <Pagination className="clear" totalItems={totalProducts} pageSize={pageSize} />
                    </div>
                </div>
            </CheckoutContainerInner>
        </DocumentTitle>
    );
};
